import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { DualListItem } from "components/DualList";
import { useNavigation } from "contexts/notificationsContext";
import { api, apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import BackdropLoading from "components/BackdropLoading";

interface ModalLiberacaoPadraoProps {
  open: boolean;
  onClose: () => void;
  liberacaoId?: number;
  onSave: () => void;
  nomesExistentes?: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`liberacao-tabpanel-${index}`}
      aria-labelledby={`liberacao-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ModalLiberacaoPadrao: React.FC<ModalLiberacaoPadraoProps> = ({
  open,
  onClose,
  liberacaoId,
  onSave,
  nomesExistentes = [],
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  const isNovo = !liberacaoId;

  // Snapshot dos campos do cabeçalho no momento da carga — as abas salvam
  // automaticamente, mas Nome/Sistema/Patrocínio só persistem no SALVAR.
  const dadosIniciaisRef = useRef<{ nome: string; sistema: string; patrocinio: number | "" }>({
    nome: "",
    sistema: "",
    patrocinio: "",
  });

  // Timers para debounce do salvamento automático
  const timeoutPacotesRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutTipologiasRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutAplicabilidadesRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutTabelasRef = useRef<NodeJS.Timeout | null>(null);

  // Campos do formulário
  const [nomeLiberacao, setNomeLiberacao] = useState("");
  const [sistema, setSistema] = useState("");
  const [patrocinioId, setPatrocinioId] = useState<number | "">("");

  // Opções
  const [sistemasOpcoes] = useState([
    { value: "CEM", label: "CEM" },
    { value: "CEM Smart", label: "CEM Smart" },
    { value: "CEM Mini", label: "CEM Mini" },
    { value: "ONE", label: "ONE" },
    // Grava "CMB" (valor que a rotina de auto-liberação do MOBI procura em
    // LiberacaoPadraoCEM.Sistema — Q3 da Nathália, 22/06) e exibe "CEM MOBI".
    { value: "CMB", label: "CEM MOBI" },
  ]);
  const [patrocinios, setPatrocinios] = useState<Array<{ idPatrocinio: number; descricao: string }>>([]);
  // Plano 21/10/2026 (Frente 2): patrocínio 0 é o padrão (sem patrocinador) — o legado grava 0 quando
  // não há patrocínio, e a Extranet não traz essa opção.
  const opcoesPatrocinio = [
    { idPatrocinio: 0, descricao: "Sem patrocínio (padrão)" },
    ...patrocinios.filter((p) => p.idPatrocinio !== 0),
  ];

  // CADASTROS N1 - Pacote Atualizações
  const [pacotesDisponiveis, setPacotesDisponiveis] = useState<DualListItem[]>([]);
  const [pacotesSelecionados, setPacotesSelecionados] = useState<DualListItem[]>([]);
  const [buscaN1, setBuscaN1] = useState("");

  // CADASTROS N2 - Tipologias
  const [tipologiasDisponiveis, setTipologiasDisponiveis] = useState<DualListItem[]>([]);
  const [tipologiasSelecionadas, setTipologiasSelecionadas] = useState<DualListItem[]>([]);
  const [buscaN2, setBuscaN2] = useState("");

  // APLICABILIDADES
  const [aplicabilidadesDisponiveis, setAplicabilidadesDisponiveis] = useState<DualListItem[]>([]);
  const [aplicabilidadesSelecionadas, setAplicabilidadesSelecionadas] = useState<DualListItem[]>([]);
  const [buscaAplicabilidade, setBuscaAplicabilidade] = useState("");

  // TABELA DE PREÇOS
  const [tabelasDisponiveis, setTabelasDisponiveis] = useState<DualListItem[]>([]);
  const [tabelasSelecionadas, setTabelasSelecionadas] = useState<DualListItem[]>([]);
  const [buscaTabela, setBuscaTabela] = useState("");

  // Salvamento automático (debounced)
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  // Plano 15/10/2026 (Frente 2): cada carga recebe um número; fechar o modal invalida a carga em
  // andamento — respostas atrasadas são descartadas e o "carregando" não sobra sobre a página.
  const cargaAtualRef = useRef(0);
  // Referência de cada pacote (para agrupar o salvar por referência) — lida uma vez por abertura
  const referenciaPorPacoteRef = useRef<Map<number, number> | null>(null);
  // Seleções como vieram do banco. O MOF antigo regrava Tipologias/Aplicabilidades/Tabelas
  // apagando tudo e expandindo (par → todas as tipologias; empresa → todas as referências):
  // o Salvar só reenvia a aba que o usuário alterou, para "abrir e salvar" não mudar dados.
  const selecaoCarregadaRef = useRef({ tipologias: "", aplicabilidades: "", tabelas: "" });
  const chaveSelecao = (lista: DualListItem[]) => lista.map((i) => String(i.id)).sort().join(",");

  useEffect(() => {
    if (open) {
      referenciaPorPacoteRef.current = null;
      carregarPatrocinios();
      if (liberacaoId) {
        carregarDadosLiberacao();
      } else {
        resetarFormulario();
      }
    } else {
      cargaAtualRef.current += 1;
      setIsLoading(false);
    }
  }, [open, liberacaoId]);

  // Agrupa os pacotes marcados por referência — o POST do MOF antigo (liberacaoPortfolio) grava
  // uma referência por vez. Antes eram 316 GETs PacoteAtualizacaoSelect, um por referência.
  const agruparPacotesPorReferencia = async (pacoteIds: number[]): Promise<Map<number, number[]>> => {
    if (!referenciaPorPacoteRef.current) {
      const { data } = await api.get<Array<{ id: number; idReferencia: number }>>(
        "LiberacaoPadraoCemPacotes/Referencias"
      );
      referenciaPorPacoteRef.current = new Map((data || []).map((p) => [Number(p.id), Number(p.idReferencia)]));
    }
    const porReferencia = new Map<number, number[]>();
    for (const idPacote of pacoteIds) {
      const idReferencia = referenciaPorPacoteRef.current.get(Number(idPacote));
      if (idReferencia === undefined) continue;
      porReferencia.set(idReferencia, [...(porReferencia.get(idReferencia) || []), Number(idPacote)]);
    }
    return porReferencia;
  };

  const resetarFormulario = () => {
    setNomeLiberacao("");
    setSistema("");
    setPatrocinioId("");
    setPacotesSelecionados([]);
    setTipologiasSelecionadas([]);
    setAplicabilidadesSelecionadas([]);
    setTabelasSelecionadas([]);
    setActiveTab(0);
    dadosIniciaisRef.current = { nome: "", sistema: "", patrocinio: "" };
    selecaoCarregadaRef.current = { tipologias: "", aplicabilidades: "", tabelas: "" };
  };

  const carregarPatrocinios = async () => {
    try {
      const { data } = await apiAntigo.get("/geral/ObterPatrociniosExtranet");
      const lista = Array.isArray(data) ? data : [];
      setPatrocinios(lista);
      if (lista.length === 0) {
        // Decisão do PO (19/06/2026): sem fallback — dropdown vazio + aviso explícito
        openNotification({ message: "Falha na integração com a Extranet", type: "error" });
      }
    } catch (error) {
      console.error("Erro ao carregar patrocínios", error);
      setPatrocinios([]);
      openNotification({ message: "Falha na integração com a Extranet", type: "error" });
    }
  };


  const carregarDadosLiberacao = async () => {
    if (!liberacaoId) return;
    const carga = ++cargaAtualRef.current;
    const cargaValida = () => carga === cargaAtualRef.current;
    selecaoCarregadaRef.current = { tipologias: "", aplicabilidades: "", tabelas: "" };
    setIsLoading(true);
    try {
      const { data } = await apiAntigo.get(`/LiberacaoPadraoCEM/liberacao/${liberacaoId}`);
      if (!cargaValida()) return;
      setNomeLiberacao(data.descricao || "");
      setSistema(data.sistema?.value || "");
      // Plano 21/10/2026 (Frente 2): 0 = "Sem patrocínio (padrão)" — com || o 0 virava vazio e as
      // 5 liberações padrão gerais nunca passavam na validação do Salvar.
      setPatrocinioId(data.patrocinio?.value ?? "");
      dadosIniciaisRef.current = {
        nome: data.descricao || "",
        sistema: data.sistema?.value || "",
        patrocinio: data.patrocinio?.value ?? "",
      };

      // Carregar dados das abas
      // Primeiro carregar listas disponíveis
      const [, tipologiasDisp, tabelasDisp, aplicabilidadesDisp] = await Promise.all([
        carregarPacotesGeral(cargaValida),
        carregarTipologiasDisponiveis(),
        carregarTabelasDisponiveis(),
        carregarAplicabilidadesDisponiveis(),
      ]);
      if (!cargaValida()) return;

      // Depois carregar seleções. Plano 15/10/2026 (Frente 2): as listas vão por parâmetro — lidas
      // do estado, elas ainda estavam vazias nesta mesma carga e nada aparecia marcado.
      await Promise.all([
        carregarTipologiasSelecionadas(tipologiasDisp, cargaValida),
        carregarAplicabilidadesSelecionadas(aplicabilidadesDisp, cargaValida),
        carregarTabelasSelecionadas(tabelasDisp, cargaValida),
      ]);
    } catch (error: any) {
      if (!cargaValida()) return;
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      if (cargaValida()) setIsLoading(false);
    }
  };

  const carregarPacotesGeral = async (cargaValida: () => boolean = () => true) => {
    if (!liberacaoId) return;
    try {
      // Carregar Tipo + Referência + Linha (igual à tela de Liberações Diversas)
      const { data: todosPacotes } = await apiAntigo.get("/geral/SelecaoPacoteComLinha");
      
      // Mapear todos os pacotes primeiro
      const todosMapeados: DualListItem[] = (todosPacotes || []).map((p: any) => ({
        id: p.value || p.Value,
        label: p.label || p.Label || "",
      }));

      // Filtrar para excluir Tipologia (TipoPacote = 0) - manter apenas Referência (1), Linha (2) e Acabamento (3)
      const disponiveis: DualListItem[] = todosMapeados.filter((item: DualListItem) => {
        if (!item.label || !item.id) {
          return false;
        }
        
        const label = item.label.trim();
        if (!label) {
          return false;
        }
        
        // Converter para maiúsculas para comparação case-insensitive
        const labelUpper = label.toUpperCase();
        
        // Excluir Tipologia (TipoPacote = 0) - que começa com "TIPOLOGIA"
        if (labelUpper.startsWith("TIPOLOGIA")) {
          return false;
        }
        
        // Incluir se começar com Referência (1), Linha (2) ou Acabamento (3)
        const isReferencia = labelUpper.startsWith("REFERÊNCIA") || labelUpper.startsWith("REFERENCIA");
        const isLinha = labelUpper.startsWith("LINHA");
        const isAcabamento = labelUpper.startsWith("ACABAMENTO");
        
        return isReferencia || isLinha || isAcabamento;
      });

      if (!cargaValida()) return;
      setPacotesDisponiveis(disponiveis);

      // Plano 15/10/2026 (Frente 2): os pacotes já gravados vêm numa chamada só ao MOF novo — antes
      // era 1 GET ao MOF antigo por referência, em sequência (316 chamadas, ~94 s).
      const { data: selecionados } = await api.get<number[]>(`LiberacaoPadraoCemPacotes/${liberacaoId}`);
      if (!cargaValida()) return;
      const idsSelecionados = new Set((selecionados || []).map((id) => Number(id)));

      // Filtrar os pacotes disponíveis que estão selecionados
      const selecionadosList: DualListItem[] = disponiveis.filter((item) =>
        idsSelecionados.has(Number(item.id))
      );

      setPacotesSelecionados(selecionadosList);
    } catch (error) {
      console.error("Erro ao carregar pacotes", error);
    }
  };

  const carregarTipologiasDisponiveis = async (): Promise<DualListItem[]> => {
    try {
      // Carregar Projetistas + Linha/Sistema disponíveis (igual à tela de Liberações Diversas)
      const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/ListarProjetistasLinhasDisponiveis");
      
      if (data && Array.isArray(data) && data.length > 0) {
        const projetistasLinhas: DualListItem[] = data.map((item: any) => ({
          id: item.value || item.Value || "",
          label: item.label || item.Label || "",
        })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos
        
        setTipologiasDisponiveis(projetistasLinhas);
        return projetistasLinhas;
      } else {
        console.warn("Resposta inválida ou vazia do endpoint ListarProjetistasLinhasDisponiveis:", data);
        setTipologiasDisponiveis([]);
        return [];
      }
    } catch (error: any) {
      console.error("Erro ao carregar Projetistas + Linha/Sistema:", error);
      openNotification({ 
        message: error.response?.data?.message || "Erro ao carregar Projetistas + Linha/Sistema.", 
        type: "error" 
      });
      setTipologiasDisponiveis([]);
      return [];
    }
  };

  const carregarTipologiasSelecionadas = async (
    tipologiasDisponiveis: DualListItem[],
    cargaValida: () => boolean = () => true
  ) => {
    if (!liberacaoId) return;
    try {
      // Buscar IDs de tipologias já selecionadas
      const { data } = await apiAntigo.get(
        `/LiberacaoPadraoCEM/liberacaoTipologiaDisponivel/${liberacaoId}`
      );
      const idsTipologiasSelecionadas: number[] =
        (data && (data.selectedTipologias as number[])) ||
        (Array.isArray(data) ? (data as number[]) : []);

      if (!idsTipologiasSelecionadas.length || !tipologiasDisponiveis.length) {
        if (cargaValida()) setTipologiasSelecionadas([]);
        return;
      }

      // Para cada combinação ProjetistaId_LinhaId disponível, verificar se tem tipologias selecionadas
      const selecionadas: DualListItem[] = [];
      
      for (const itemDisponivel of tipologiasDisponiveis) {
        // Modal fechado/reaberto: para de consultar o MOF antigo para uma carga descartada
        if (!cargaValida()) return;
        const idStr = String(itemDisponivel.id);
        if (idStr.includes("_")) {
          const [projetistaIdStr, linhaIdStr] = idStr.split("_");
          const projetistaId = parseInt(projetistaIdStr, 10);
          const linhaId = parseInt(linhaIdStr, 10);
          
          if (!isNaN(projetistaId) && !isNaN(linhaId)) {
            try {
              // Buscar tipologias dessa combinação
              const { data: tipologiasData } = await apiAntigo.get("/LiberacaoPadraoCEM/listarTipologias", {
                params: { projetistaId, linhaId }
              });
              
              if (tipologiasData && Array.isArray(tipologiasData)) {
                // Verificar se alguma tipologia dessa combinação está selecionada
                const temTipologiaSelecionada = tipologiasData.some((t: any) => {
                  const idTipologia = t.id || t.Id || t.value || t.Value;
                  return idsTipologiasSelecionadas.includes(Number(idTipologia));
                });
                
                if (temTipologiaSelecionada) {
                  selecionadas.push(itemDisponivel);
                }
              }
            } catch (error) {
              console.error(`Erro ao verificar tipologias para Projetista ${projetistaId} e Linha ${linhaId}:`, error);
            }
          }
        }
      }

      if (cargaValida()) {
        setTipologiasSelecionadas(selecionadas);
        selecaoCarregadaRef.current.tipologias = chaveSelecao(selecionadas);
      }
    } catch (error) {
      console.error("Erro ao carregar tipologias selecionadas", error);
      if (cargaValida()) setTipologiasSelecionadas([]);
    }
  };

  const carregarAplicabilidadesSelecionadas = async (
    aplicabilidadesDisponiveis: DualListItem[],
    cargaValida: () => boolean = () => true
  ) => {
    if (!liberacaoId) return;
    try {
      const { data } = await apiAntigo.get(
        `/LiberacaoPadraoCEM/liberacaoAplicabilidadeDisponivel/${liberacaoId}`
      );
      // Plano 15/10/2026 (Frente 2): o MOF antigo devolve { selectedAplicabilidade: [...] }, não a
      // lista pura — lido como array, vinha sempre vazio.
      const lista = Array.isArray(data) ? data : data?.selectedAplicabilidade;
      const idsSelecionados: number[] = Array.isArray(lista) ? lista.map((id: any) => Number(id)) : [];
      if (!cargaValida()) return;

      if (!idsSelecionados.length || !aplicabilidadesDisponiveis.length) {
        setAplicabilidadesSelecionadas([]);
        return;
      }

      const selecionadas = aplicabilidadesDisponiveis.filter((a) =>
        idsSelecionados.includes(Number(a.id))
      );

      setAplicabilidadesSelecionadas(selecionadas);
      selecaoCarregadaRef.current.aplicabilidades = chaveSelecao(selecionadas);
    } catch (error) {
      console.error("Erro ao carregar aplicabilidades", error);
    }
  };

  const carregarTabelasDisponiveis = async (): Promise<DualListItem[]> => {
    try {
      const { data } = await apiAntigo.get("/geral/SelecaoTabelaPrecoDisponivelDasEmpresas");
      const disponiveis: DualListItem[] = (data || []).map((item: any) => ({
        id: item.value || item.id || item.Id,
        label: item.label || item.descricao || item.Descricao || "-",
        // Empresa dona da tabela: exibida como subtítulo e usada pelo filtro
        // (filtrarLista já busca em label e subtitle) — a lista segue mostrando
        // os títulos das tabelas, filtrando pela empresa digitada.
        subtitle: item.empresa || item.Empresa || undefined,
      }));
      setTabelasDisponiveis(disponiveis);
      return disponiveis;
    } catch (error) {
      console.error("Erro ao carregar tabelas disponíveis", error);
      return [];
    }
  };

  const carregarTabelasSelecionadas = async (
    tabelasDisponiveis: DualListItem[],
    cargaValida: () => boolean = () => true
  ) => {
    if (!liberacaoId) return;
    try {
      const { data } = await apiAntigo.get(
        `/LiberacaoPadraoCEM/liberacaoTabelaPrecoDisponivel/${liberacaoId}`
      );
      // O MOF antigo devolve { selectedTabela: [...] } (mesmo caso das aplicabilidades)
      const lista = Array.isArray(data) ? data : data?.selectedTabela;
      const idsSelecionados: number[] = Array.isArray(lista) ? lista.map((id: any) => Number(id)) : [];
      if (!cargaValida()) return;

      if (!idsSelecionados.length || !tabelasDisponiveis.length) {
        setTabelasSelecionadas([]);
        return;
      }

      const selecionadas = tabelasDisponiveis.filter((t) =>
        idsSelecionados.includes(Number(t.id))
      );

      setTabelasSelecionadas(selecionadas);
      selecaoCarregadaRef.current.tabelas = chaveSelecao(selecionadas);
    } catch (error) {
      console.error("Erro ao carregar tabelas", error);
    }
  };

  const carregarAplicabilidadesDisponiveis = async (): Promise<DualListItem[]> => {
    try {
      const { data } = await apiAntigo.get("/geral/referenciaAplicabilidadeExistente");
      const disponiveis: DualListItem[] = (data || []).map((item: any) => ({
        id: item.value || item.id || item.Id,
        label: item.label || item.descricao || item.Descricao || "-",
      }));
      setAplicabilidadesDisponiveis(disponiveis);
      return disponiveis;
    } catch (error) {
      console.error("Erro ao carregar aplicabilidades disponíveis", error);
      return [];
    }
  };


  // Carregar listas disponíveis quando o modal abrir
  useEffect(() => {
    if (open && !liberacaoId) {
      // Em modo de criação, carregar as listas disponíveis
      carregarPacotesDisponiveisInicial();
      carregarTabelasDisponiveis();
      carregarAplicabilidadesDisponiveis();
      carregarTipologiasDisponiveis();
    }
  }, [open, liberacaoId]);

  const carregarPacotesDisponiveisInicial = async () => {
    try {
      // Carregar Tipo + Referência + Linha (igual à tela de Liberações Diversas)
      const { data: todosPacotes } = await apiAntigo.get("/geral/SelecaoPacoteComLinha");
      
      // Mapear todos os pacotes primeiro
      const todosMapeados: DualListItem[] = (todosPacotes || []).map((p: any) => ({
        id: p.value || p.Value,
        label: p.label || p.Label || "",
      }));

      // Filtrar para excluir Tipologia (TipoPacote = 0) - manter apenas Referência (1), Linha (2) e Acabamento (3)
      const disponiveis: DualListItem[] = todosMapeados.filter((item: DualListItem) => {
        if (!item.label || !item.id) {
          return false;
        }
        
        const label = item.label.trim();
        if (!label) {
          return false;
        }
        
        // Converter para maiúsculas para comparação case-insensitive
        const labelUpper = label.toUpperCase();
        
        // Excluir Tipologia (TipoPacote = 0) - que começa com "TIPOLOGIA"
        if (labelUpper.startsWith("TIPOLOGIA")) {
          return false;
        }
        
        // Incluir se começar com Referência (1), Linha (2) ou Acabamento (3)
        const isReferencia = labelUpper.startsWith("REFERÊNCIA") || labelUpper.startsWith("REFERENCIA");
        const isLinha = labelUpper.startsWith("LINHA");
        const isAcabamento = labelUpper.startsWith("ACABAMENTO");
        
        return isReferencia || isLinha || isAcabamento;
      });

      setPacotesDisponiveis(disponiveis);
    } catch (error) {
      console.error("Erro ao carregar pacotes disponíveis", error);
    }
  };

  // Carregar tipologias quando a aba N2 for aberta
  useEffect(() => {
    if (open && activeTab === 1 && tipologiasDisponiveis.length === 0) {
      carregarTipologiasDisponiveis();
    }
  }, [open, activeTab]);

  const temAlteracoesNaoSalvas = () =>
    nomeLiberacao !== dadosIniciaisRef.current.nome ||
    sistema !== dadosIniciaisRef.current.sistema ||
    patrocinioId !== dadosIniciaisRef.current.patrocinio;

  const handleFechar = () => {
    if (temAlteracoesNaoSalvas()) {
      setConfirmarDescarte(true);
      return;
    }
    onClose();
  };

  const handleSalvar = async () => {
    if (!nomeLiberacao.trim() || !sistema || patrocinioId === "") {
      openNotification({
        message: "Preencha Nome da Liberação, Sistema e Patrocínio.",
        type: "error",
      });
      return;
    }

    if (nomeLiberacao.trim().length < 3) {
      openNotification({
        message: "O nome da liberação deve ter pelo menos 3 caracteres.",
        type: "error",
      });
      return;
    }

    const nomeNormalizado = nomeLiberacao.trim().toLowerCase();
    if (nomesExistentes.some((n) => n?.trim().toLowerCase() === nomeNormalizado)) {
      openNotification({
        message: "Já existe uma liberação padrão com esse nome.",
        type: "error",
      });
      return;
    }

    const sistemaInfo = sistemasOpcoes.find((s) => s.value === sistema);
    const patrocinioInfo = opcoesPatrocinio.find((p) => p.idPatrocinio === patrocinioId);

    const dados = {
      id: liberacaoId || 0,
      descricao: nomeLiberacao.trim(),
      sistema: {
        label: sistemaInfo?.label ?? sistema,
        value: sistema,
      },
      patrocinio: patrocinioInfo
        ? {
            label: patrocinioInfo.descricao,
            value: patrocinioInfo.idPatrocinio,
          }
        : {
            // Extranet fora do ar: mantém o patrocínio gravado em vez de trocá-lo por 0
            label: "",
            value: Number(patrocinioId),
          },
    };

    setIsLoading(true);
    try {
      const response = await apiAntigo.post("/LiberacaoPadraoCEM", dados);
      const novoId = response.data?.data || liberacaoId;

      // Salvar dados das abas (tanto criação quanto edição)
      if (novoId) {
        await Promise.all([
          pacotesSelecionados.length > 0 ? salvarPacotes(novoId) : Promise.resolve(),
          // Plano 15/10/2026 (Frente 2): só a aba alterada nesta abertura é regravada
          tipologiasSelecionadas.length > 0 && chaveSelecao(tipologiasSelecionadas) !== selecaoCarregadaRef.current.tipologias
            ? salvarTipologias(novoId) : Promise.resolve(),
          aplicabilidadesSelecionadas.length > 0 && chaveSelecao(aplicabilidadesSelecionadas) !== selecaoCarregadaRef.current.aplicabilidades
            ? salvarAplicabilidades(novoId) : Promise.resolve(),
          tabelasSelecionadas.length > 0 && chaveSelecao(tabelasSelecionadas) !== selecaoCarregadaRef.current.tabelas
            ? salvarTabelas(novoId) : Promise.resolve(),
        ]);
      }

      openNotification({
        message: `Liberação padrão ${isNovo ? "criada" : "atualizada"} com sucesso!`,
        type: "success",
      });
      onSave();
      onClose();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const salvarPacotes = async (id: number) => {
    try {
      // Agrupar pacotes por IdReferencia
      const pacoteIds = pacotesSelecionados.map((p) => Number(p.id));
      
      if (pacoteIds.length === 0) {
        return;
      }

      // Plano 15/10/2026 (Frente 2): agrupa por referência com um mapa só (antes, 316 GETs)
      const pacotesPorReferencia = await agruparPacotesPorReferencia(pacoteIds);

      // Salvar cada referência separadamente (em paralelo)
      const promessasSalvar = Array.from(pacotesPorReferencia.entries()).map(
        async ([idReferencia, idsPacotes]) => {
          try {
            await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoPortfolio", {
              IdReferencia: idReferencia,
              IdLiberacaoPadrao: id,
              IdsAtualizacaoPortfolio: idsPacotes,
            });
          } catch (error) {
            console.error(`Erro ao salvar pacotes para referência ${idReferencia}:`, error);
            throw error;
          }
        }
      );

      await Promise.all(promessasSalvar);
    } catch (error) {
      console.error("Erro ao salvar pacotes", error);
      throw error;
    }
  };

  const salvarTipologias = async (id: number) => {
    try {
      // Converter "ProjetistaId_LinhaId" em IDs de tipologias
      // O formato do id é "ProjetistaId_LinhaId" (ex: "1_478")
      const idsTipologia: number[] = [];
      
      for (const item of tipologiasSelecionadas) {
        const idStr = String(item.id);
        if (idStr.includes("_")) {
          // Formato "ProjetistaId_LinhaId"
          const [projetistaIdStr, linhaIdStr] = idStr.split("_");
          const projetistaId = parseInt(projetistaIdStr, 10);
          const linhaId = parseInt(linhaIdStr, 10);
          
          if (!isNaN(projetistaId) && !isNaN(linhaId)) {
            try {
              // Buscar todas as tipologias dessa combinação
              const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/listarTipologias", {
                params: { projetistaId, linhaId }
              });
              
              if (data && Array.isArray(data)) {
                const tipologiaIds = data.map((t: any) => 
                  t.id || t.Id || t.idTipologia || t.IdTipologia || t.value || t.Value
                ).filter((id: any) => id != null);
                idsTipologia.push(...tipologiaIds);
              }
            } catch (error) {
              console.error(`Erro ao buscar tipologias para Projetista ${projetistaId} e Linha ${linhaId}:`, error);
            }
          }
        } else {
          // Se não for formato "ProjetistaId_LinhaId", assumir que é ID de tipologia direto (compatibilidade)
          const tipologiaId = Number(idStr);
          if (!isNaN(tipologiaId)) {
            idsTipologia.push(tipologiaId);
          }
        }
      }

      // Remover duplicatas
      const idsTipologiaUnicos = Array.from(new Set(idsTipologia));

      if (idsTipologiaUnicos.length > 0) {
        await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoTipologia", {
          idLiberacaoPadrao: id,
          idsTipologia: idsTipologiaUnicos,
        });
      }
    } catch (error) {
      console.error("Erro ao salvar tipologias", error);
      throw error;
    }
  };

  const salvarAplicabilidades = async (id: number) => {
    try {
      await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoAplicabilidade", {
        idLiberacaoPadrao: id,
        idsReferencia: aplicabilidadesSelecionadas.map((a) => Number(a.id)),
      });
    } catch (error) {
      console.error("Erro ao salvar aplicabilidades", error);
    }
  };

  const salvarTabelas = async (id: number) => {
    try {
      await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoTabelaPreco", {
        idLiberacaoPadrao: id,
        idsTabelaPreco: tabelasSelecionadas.map((t) => Number(t.id)),
      });
    } catch (error) {
      console.error("Erro ao salvar tabelas", error);
    }
  };

  const filtrarLista = (lista: DualListItem[], busca: string): DualListItem[] => {
    if (!busca.trim()) return lista;
    const buscaLower = busca.toLowerCase();
    return lista.filter(
      (item) =>
        item.label?.toLowerCase().includes(buscaLower) ||
        item.subtitle?.toLowerCase().includes(buscaLower)
    );
  };

  // Salvamento automático com debounce
  const handleSalvarPacotesAuto = useCallback(async (newSelection: DualListItem[]) => {
    if (timeoutPacotesRef.current) {
      clearTimeout(timeoutPacotesRef.current);
    }
    timeoutPacotesRef.current = setTimeout(async () => {
      try {
        // Agrupar pacotes por IdReferencia (mesma lógica do salvarPacotes)
        const pacoteIds = newSelection.map((p) => Number(p.id));
        
        if (pacoteIds.length === 0) {
          return;
        }

        // Plano 15/10/2026 (Frente 2): agrupa por referência com um mapa só (antes, 316 GETs)
        const pacotesPorReferencia = await agruparPacotesPorReferencia(pacoteIds);

        // Salvar cada referência separadamente (em paralelo)
        const promessasSalvar = Array.from(pacotesPorReferencia.entries()).map(
          async ([idReferencia, idsPacotes]) => {
            try {
              await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoPortfolio", {
                IdReferencia: idReferencia,
                IdLiberacaoPadrao: liberacaoId,
                IdsAtualizacaoPortfolio: idsPacotes,
              });
            } catch (error) {
              console.error(`Erro ao salvar pacotes para referência ${idReferencia}:`, error);
            }
          }
        );

        await Promise.all(promessasSalvar);
        console.log("✅ Pacotes salvos automaticamente");
      } catch (error) {
        console.error("Erro ao salvar pacotes:", error);
      }
    }, 800);
  }, [liberacaoId]);

  const handleSalvarTipologiasAuto = useCallback(async (newSelection: DualListItem[]) => {
    if (timeoutTipologiasRef.current) {
      clearTimeout(timeoutTipologiasRef.current);
    }
    timeoutTipologiasRef.current = setTimeout(async () => {
      try {
        // Converter "ProjetistaId_LinhaId" em IDs de tipologias (mesma lógica do salvarTipologias)
        const idsTipologia: number[] = [];
        
        for (const item of newSelection) {
          const idStr = String(item.id);
          if (idStr.includes("_")) {
            // Formato "ProjetistaId_LinhaId"
            const [projetistaIdStr, linhaIdStr] = idStr.split("_");
            const projetistaId = parseInt(projetistaIdStr, 10);
            const linhaId = parseInt(linhaIdStr, 10);
            
            if (!isNaN(projetistaId) && !isNaN(linhaId)) {
              try {
                const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/listarTipologias", {
                  params: { projetistaId, linhaId }
                });
                
                if (data && Array.isArray(data)) {
                  const tipologiaIds = data.map((t: any) => 
                    t.id || t.Id || t.idTipologia || t.IdTipologia || t.value || t.Value
                  ).filter((id: any) => id != null);
                  idsTipologia.push(...tipologiaIds);
                }
              } catch (error) {
                console.error(`Erro ao buscar tipologias para Projetista ${projetistaId} e Linha ${linhaId}:`, error);
              }
            }
          } else {
            // Compatibilidade: se não for formato "ProjetistaId_LinhaId", assumir que é ID de tipologia direto
            const tipologiaId = Number(idStr);
            if (!isNaN(tipologiaId)) {
              idsTipologia.push(tipologiaId);
            }
          }
        }

        // Remover duplicatas
        const idsTipologiaUnicos = Array.from(new Set(idsTipologia));

        if (idsTipologiaUnicos.length > 0) {
          await apiAntigo.post("/LiberacaoPadraoCEM/liberacaoTipologia", {
            idLiberacaoPadrao: liberacaoId,
            idsTipologia: idsTipologiaUnicos,
          });
          console.log("✅ Tipologias salvas automaticamente");
        }
      } catch (error) {
        console.error("Erro ao salvar tipologias:", error);
      }
    }, 800);
  }, [liberacaoId]);

  const handleSalvarAplicabilidadesAuto = useCallback((newSelection: DualListItem[]) => {
    if (timeoutAplicabilidadesRef.current) {
      clearTimeout(timeoutAplicabilidadesRef.current);
    }
    timeoutAplicabilidadesRef.current = setTimeout(async () => {
      try {
        const aplicabilidadeIds = newSelection.map((a) => a.id);
        await apiAntigo.post(
          `/LiberacaoPadraoCEM/liberacaoAplicabilidade?idLiberacao=${liberacaoId}`,
          aplicabilidadeIds
        );
        console.log("✅ Aplicabilidades salvas automaticamente");
      } catch (error) {
        console.error("Erro ao salvar aplicabilidades:", error);
      }
    }, 800);
  }, [liberacaoId]);

  const handleSalvarTabelasAuto = useCallback((newSelection: DualListItem[]) => {
    if (timeoutTabelasRef.current) {
      clearTimeout(timeoutTabelasRef.current);
    }
    timeoutTabelasRef.current = setTimeout(async () => {
      try {
        const tabelaIds = newSelection.map((t) => t.id);
        await apiAntigo.post(
          `/LiberacaoPadraoCEM/liberacaoTabelaPreco?idLiberacao=${liberacaoId}`,
          tabelaIds
        );
        console.log("✅ Tabelas salvas automaticamente");
      } catch (error) {
        console.error("Erro ao salvar tabelas:", error);
      }
    }, 800);
  }, [liberacaoId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleFechar}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: "800px",
          }
        }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          {isNovo ? "Adicionar" : "Editar"} liberações padrões
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Card superior com campos principais */}
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 3,
                mb: 3,
                bgcolor: "background.paper",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome da liberação *"
                    value={nomeLiberacao}
                    onChange={(e) => setNomeLiberacao(e.target.value)}
                    helperText="Obrigatório"
                    FormHelperTextProps={{
                      sx: { color: "#3C90AD" }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Sistema *"
                    value={sistema}
                    onChange={(e) => setSistema(e.target.value)}
                    helperText="Obrigatório"
                    FormHelperTextProps={{
                      sx: { color: "#3C90AD" }
                    }}
                  >
                    {sistemasOpcoes.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Patrocínio *"
                    value={patrocinioId}
                    onChange={(e) =>
                      setPatrocinioId(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    helperText="Obrigatório"
                    FormHelperTextProps={{
                      sx: { color: "#3C90AD" }
                    }}
                  >
                    {opcoesPatrocinio.map((p) => (
                      <MenuItem key={p.idPatrocinio} value={p.idPatrocinio}>
                        {p.descricao}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            {/* Card com abas */}
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  px: 2,
                  bgcolor: "rgba(235, 244, 249, 1)",
                  "& .MuiTab-root": {
                    color: "#75B0C7",
                    bgcolor: "rgba(235, 244, 249, 1)",
                    "&.Mui-selected": {
                      color: "#2F7EA0",
                      bgcolor: "rgba(53, 131, 177, 0.08)",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#2F7EA0",
                  },
                }}
              >
                <Tab label="CADASTROS N1" id="liberacao-tab-0" aria-controls="liberacao-tabpanel-0" />
                <Tab label="CADASTROS N2" id="liberacao-tab-1" aria-controls="liberacao-tabpanel-1" />
                <Tab
                  label="APLICABILIDADES"
                  id="liberacao-tab-2"
                  aria-controls="liberacao-tabpanel-2"
                />
                <Tab
                  label="TABELA DE PREÇOS"
                  id="liberacao-tab-3"
                  aria-controls="liberacao-tabpanel-3"
                />
              </Tabs>

            {/* Tab CADASTROS N1 */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Tipo + Referência + Linha
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Busque o tipo, referência ou linha"
                  value={buscaN1}
                  onChange={(e) => setBuscaN1(e.target.value)}
                  size="small"
                  sx={{ 
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& fieldset": {
                        borderColor: "rgba(226, 232, 240, 0.8)",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {filtrarLista(pacotesDisponiveis, buscaN1).map((item) => {
                  const isChecked = pacotesSelecionados.some((p) => p.id === item.id);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": { borderBottom: 0 },
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <FormControlLabel
                        sx={{ 
                          width: "100%", 
                          m: 0, 
                          py: 0.5, 
                          px: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          "& .MuiFormControlLabel-label": {
                            flex: 1,
                            marginLeft: 0,
                            marginRight: 1,
                          },
                        }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              let newSelection: DualListItem[];
                              if (e.target.checked) {
                                newSelection = [...pacotesSelecionados, item];
                              } else {
                                newSelection = pacotesSelecionados.filter((p) => p.id !== item.id);
                              }
                              setPacotesSelecionados(newSelection);
                              if (liberacaoId) {
                                handleSalvarPacotesAuto(newSelection);
                              }
                            }}
                            size="small"
                            sx={{
                              color: "#75B0C7",
                              "&.Mui-checked": {
                                color: "#3C90AD",
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ color: "#3C90AD" }}>{item.label}</Typography>
                            {item.subtitle && (
                              <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                {item.subtitle}
                              </Typography>
                            )}
                          </Box>
                        }
                        labelPlacement="start"
                      />
                    </Box>
                  );
                })}
                {filtrarLista(pacotesDisponiveis, buscaN1).length === 0 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum item encontrado
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Tab CADASTROS N2 */}
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Projetistas + Linha/Sistema
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Busque o projetista ou linha"
                  value={buscaN2}
                  onChange={(e) => setBuscaN2(e.target.value)}
                  size="small"
                  sx={{ 
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& fieldset": {
                        borderColor: "rgba(226, 232, 240, 0.8)",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {filtrarLista(tipologiasDisponiveis, buscaN2).map((item) => {
                  const isChecked = tipologiasSelecionadas.some((t) => t.id === item.id);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": { borderBottom: 0 },
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <FormControlLabel
                        sx={{ 
                          width: "100%", 
                          m: 0, 
                          py: 0.5, 
                          px: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          "& .MuiFormControlLabel-label": {
                            flex: 1,
                            marginLeft: 0,
                            marginRight: 1,
                          },
                        }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              let newSelection: DualListItem[];
                              if (e.target.checked) {
                                newSelection = [...tipologiasSelecionadas, item];
                              } else {
                                newSelection = tipologiasSelecionadas.filter((t) => t.id !== item.id);
                              }
                              setTipologiasSelecionadas(newSelection);
                              if (liberacaoId) {
                                handleSalvarTipologiasAuto(newSelection);
                              }
                            }}
                            size="small"
                            sx={{
                              color: "#75B0C7",
                              "&.Mui-checked": {
                                color: "#3C90AD",
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ color: "#3C90AD" }}>{item.label}</Typography>
                            {item.subtitle && (
                              <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                {item.subtitle}
                              </Typography>
                            )}
                          </Box>
                        }
                        labelPlacement="start"
                      />
                    </Box>
                  );
                })}
                {filtrarLista(tipologiasDisponiveis, buscaN2).length === 0 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum item encontrado
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Tab APLICABILIDADES */}
            <TabPanel value={activeTab} index={2}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Empresas
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Busque a empresa"
                  value={buscaAplicabilidade}
                  onChange={(e) => setBuscaAplicabilidade(e.target.value)}
                  size="small"
                  sx={{ 
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& fieldset": {
                        borderColor: "rgba(226, 232, 240, 0.8)",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {filtrarLista(aplicabilidadesDisponiveis, buscaAplicabilidade).map((item) => {
                  const isChecked = aplicabilidadesSelecionadas.some((a) => a.id === item.id);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": { borderBottom: 0 },
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <FormControlLabel
                        sx={{ 
                          width: "100%", 
                          m: 0, 
                          py: 0.5, 
                          px: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          "& .MuiFormControlLabel-label": {
                            flex: 1,
                            marginLeft: 0,
                            marginRight: 1,
                          },
                        }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              let newSelection: DualListItem[];
                              if (e.target.checked) {
                                newSelection = [...aplicabilidadesSelecionadas, item];
                              } else {
                                newSelection = aplicabilidadesSelecionadas.filter((a) => a.id !== item.id);
                              }
                              setAplicabilidadesSelecionadas(newSelection);
                              if (liberacaoId) {
                                handleSalvarAplicabilidadesAuto(newSelection);
                              }
                            }}
                            size="small"
                            sx={{
                              color: "#75B0C7",
                              "&.Mui-checked": {
                                color: "#3C90AD",
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ color: "#3C90AD" }}>{item.label}</Typography>
                            {item.subtitle && (
                              <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                {item.subtitle}
                              </Typography>
                            )}
                          </Box>
                        }
                        labelPlacement="start"
                      />
                    </Box>
                  );
                })}
                {filtrarLista(aplicabilidadesDisponiveis, buscaAplicabilidade).length === 0 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum item encontrado
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Tab TABELA DE PREÇOS */}
            <TabPanel value={activeTab} index={3}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Empresas
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Busque a empresa"
                  value={buscaTabela}
                  onChange={(e) => setBuscaTabela(e.target.value)}
                  size="small"
                  sx={{ 
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& fieldset": {
                        borderColor: "rgba(226, 232, 240, 0.8)",
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {filtrarLista(tabelasDisponiveis, buscaTabela).map((item) => {
                  const isChecked = tabelasSelecionadas.some((t) => t.id === item.id);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": { borderBottom: 0 },
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <FormControlLabel
                        sx={{ 
                          width: "100%", 
                          m: 0, 
                          py: 0.5, 
                          px: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          "& .MuiFormControlLabel-label": {
                            flex: 1,
                            marginLeft: 0,
                            marginRight: 1,
                          },
                        }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              let newSelection: DualListItem[];
                              if (e.target.checked) {
                                newSelection = [...tabelasSelecionadas, item];
                              } else {
                                newSelection = tabelasSelecionadas.filter((t) => t.id !== item.id);
                              }
                              setTabelasSelecionadas(newSelection);
                              if (liberacaoId) {
                                handleSalvarTabelasAuto(newSelection);
                              }
                            }}
                            size="small"
                            sx={{
                              color: "#75B0C7",
                              "&.Mui-checked": {
                                color: "#3C90AD",
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ color: "#3C90AD" }}>{item.label}</Typography>
                            {item.subtitle && (
                              <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                {item.subtitle}
                              </Typography>
                            )}
                          </Box>
                        }
                        labelPlacement="start"
                      />
                    </Box>
                  );
                })}
                {filtrarLista(tabelasDisponiveis, buscaTabela).length === 0 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum item encontrado
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFechar}>CANCELAR</Button>
          <Button variant="contained" onClick={handleSalvar} disabled={isLoading}>
            SALVAR
          </Button>
        </DialogActions>
        {/* Plano 15/10/2026 (Frente 2): o "carregando" vive dentro do modal — fora dele ficava
            atrás do diálogo enquanto aberto e cobria a página depois de fechar. */}
        <BackdropLoading isOpen={open && isLoading} />
      </Dialog>

      <Dialog open={confirmarDescarte} onClose={() => setConfirmarDescarte(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>Descartar alterações?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Há alterações não salvas em Nome, Sistema ou Patrocínio. Se sair agora, elas serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarDescarte(false)}>CONTINUAR EDITANDO</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmarDescarte(false);
              onClose();
            }}
          >
            DESCARTAR
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

